import config from "./config";
import ErrorsString from "./ErrorsString";
import { GetTemplatesModel } from "./models/GetTemplatesModel";
import { Logout } from "./models/Logout";
import { PostLogDownloadModel } from "./models/PostLogDownloadModel";
import { DownloadRequest } from "./models/RequestModels/DownloadRequest";
import {
  CropInfo,
  ImageDetails,
} from "./models/ResponseModels/DetailsImageResponse";
import { TemplateInfo } from "./models/ResponseModels/TemplatesResponse";

export const DATE_FORMAT = "yyyy-MM-DD";

export const INITIAL_SLIDE = 0;
export const SLIDES_PER_VIEW = 5;
export const EFFECT_COVERFLOW_SWIPER = {
  slideShadows: false,
  rotate: 3,
  scale: 0.75,
  stretch: -100,
  depth: 400,
};

// INTERFACES
export interface Option {
  id: number;
  label: string;
}

export interface DownloadOptions {
  resolution: string;
  ratio: string;
  previewUrl: string;
  template: string;
  uuid: string;
  fileName: string;
}

export interface AdditionalOptions {
  extension: string | null;
  color: string | null;
  maxFileSize: string | null;
}

export interface DetailsWoForBulkDownloadResponse {
  title: string;
  channel: string | null;
  contentType: string | null;
  programme: string | null;
  series: number | null;
  imageLinkedToWo: ImagesLinkedToWo;
  episodeLinkedImages: EpisodeLinkedImages;
}

export interface EpisodeLinkedImages {
  [label: string]: ImagesLinkedToWo;
}

export interface ImagesLinkedToWo {
  [label: string]: ImagesInfo[];
}

export interface ImagesInfo {
  uuid: string;
  image_info: ImageInfo[];
  proxy: string;
  thumbnail: string;
  essence: string;
  assetInternalOnly: boolean;
  assetPublicityAvailable: boolean;
  imageType: string;
  title: string;
  hasCrop: boolean;
  isGreenStar: boolean | null;
  availableTemplatesByCropGroup: TemplateByCropGroup[] | null;
  imageDetails: ImageDetails | null;
  fileName: string;
  fileExtension: string;
  linkedWorkOrderUuid: string;
  isDownloadable: boolean;
}

export interface ImageInfo {
  aspectRatio: string;
  previewUrl: string;
  cropInfo: CropInfo;
  savedTemplates: SavedTemplate[];
}

export interface SavedTemplate {
  resolution: string;
  name: string;
  id: string;
}

export interface TemplateByCropGroup {
  aspectRatio: string;
  nameTemplate: string;
  label: string;
  resolution: string;
  downlodable: boolean;
  selectedForDownload: boolean;
  previewUrl: string;
}

export interface ImageTypeDownloadable {
  [label: string]: boolean;
}

export function logDownload(uuid: string, templateName: string) {
  let datetime = new Date();

  console.log("Starting API call PostLogDownloadModel...");
  (async () => {
    const response = await PostLogDownloadModel(uuid, datetime, templateName);
    //console.log("PostLogDownloadModel response: " + JSON.stringify(response));
  })();
}

export function downloadImage(
  imageUrl: string,
  uuid: string,
  templateName: string
) {
  // download immagine a partire dalla dynamic url
  const link = document.createElement("a");
  link.href = imageUrl;
  link.download = "imageTemplate_" + templateName;
  document.body.appendChild(link);
  console.log(link.download + " : " + link);
  link.click();
  document.body.removeChild(link);

  logDownload(uuid, templateName);
}

const modifyResizeElement = (editsObj, height, width) => {
  const updatedResize = editsObj.map((item) => {
    // Se l'elemento ha type = "resize", modificalo
    if (item.type === "resize") {
      return { ...item, height: height, width: width }; // Modifica height e width per resize
    }
    return item; // Restituisci l'elemento non modificato
  });

  return updatedResize;
};

export async function getTemplatesInfoByTemplateName(
  templateName: string,
  ratio: string,
  resolution: string
) {
  console.log("Starting API call GetTemplatesModel...");

  const key = templateName + "_" + ratio + "_" + resolution;
  //console.log("key: " + key);

  try {
    const response = await GetTemplatesModel();
    const responseData = response?.data;
    const statusCode = response?.status;

    // console.log("GetTemplatesModel response: " + JSON.stringify(response));

    if (statusCode === 200) {
      const templateInfo: TemplateInfo = responseData[key];

      //console.log("GetTemplatesModel templateInfo: " + JSON.stringify(templateInfo));

      return templateInfo; // Restituisce templateInfo
    }

    return null; // Restituisce null se lo stato non è 200
  } catch (error) {
    console.error("Error fetching template info:", error);
    return null; // Gestisce errori e restituisce null
  }
}

export async function buildDynamicDownloadUrl(
  downloadOptions: DownloadOptions,
  formValues: AdditionalOptions | null
) {
  //console.log("Starting Download of template " + downloadOptions.template);
  //console.log("downloadOptions: " + JSON.stringify(downloadOptions));
  //console.log("formValues: " + JSON.stringify(formValues));

  //estraiamo dalla preview url il json con i parametri di crop da mandare alla lambda
  const url = new URL(downloadOptions.previewUrl);
  const base64Json = url.pathname.slice(1);
  let updatedParams: DownloadRequest = JSON.parse(atob(base64Json));
  //console.log("updatedParams " + JSON.stringify(updatedParams));

  //set random parameters in order to have different urls
  const randomId = Math.floor(Math.random() * 100) + 1; // Genera un numero casuale tra 1 e 100
  updatedParams.requestId = randomId;

  //get template info
  const templateName = downloadOptions["template"];
  const ratio = downloadOptions["ratio"];
  const resolution = downloadOptions["resolution"];

  let templateInfo: TemplateInfo = null;

  templateInfo = await getTemplatesInfoByTemplateName(
    templateName,
    ratio,
    resolution
  );

  if (templateInfo != null) {
    let prefix = templateInfo?.prefix ? templateInfo.prefix : "";
    const hasPrefixSeparator =
      prefix !== "" ? prefix.endsWith("_") || prefix.endsWith("-") : true;
    prefix = hasPrefixSeparator ? prefix : prefix + "_";
    let suffix = templateInfo?.suffix ? templateInfo.suffix : "";
    const hasSuffixSeparator =
      suffix !== "" ? suffix.startsWith("_") || suffix.startsWith("-") : true;
    suffix = hasSuffixSeparator ? suffix : "-" + suffix;
    let outputFileName = downloadOptions?.fileName
      ? downloadOptions.fileName
      : "";
    outputFileName = prefix + outputFileName + suffix;
    updatedParams.outputFileName = outputFileName;
  }

  //default file extension (Overwrite if the user selects an extension from the GUI)
  updatedParams.outputFormat = config.defaultFileExtension;
  //default iccProfile
  updatedParams.iccProfile = config.defaultIccProfile;

  //aggiungiamo i nuovi parametri inseriti da GUI
  if (formValues !== null) {
    if (formValues.color) {
      updatedParams.iccProfile = formValues.color;
    }

    if (formValues.maxFileSize) {
      const maxFileSizeInKb = Number(formValues.maxFileSize) * 1000;
      updatedParams.maxSizeInBytes = String(maxFileSizeInKb);
    }

    if (formValues.extension) {
      updatedParams.outputFormat = formValues.extension;
    }
  } else if (templateInfo?.maxFileSizeInKb != null) {
    updatedParams.maxSizeInBytes = String(templateInfo.maxFileSizeInKb * 1000);
  }

  //modifichiamo la resolution, specifica del templat selezionato
  const [resolutionWidth, resolutionHeight] =
    downloadOptions?.resolution.split("x");

  const updateResolution = modifyResizeElement(
    updatedParams.edits,
    Number(resolutionHeight),
    Number(resolutionWidth)
  );
  updatedParams.edits = updateResolution;

  //costrisco la download url
  const paramsString = JSON.stringify(updatedParams);
  //console.log(downloadOptions?.template + " - dynamic url params string: " + paramsString);
  const paramsBase64 = btoa(paramsString);
  const dynamicUrl = config.downloadBaseUrl + paramsBase64;
  //console.log(downloadOptions?.template + " - dynamicUrl: " + dynamicUrl);

  return dynamicUrl;
}

export async function doLogout(
  setAlertMessage: React.Dispatch<React.SetStateAction<string>>,
  setShowAlert: React.Dispatch<React.SetStateAction<boolean>>,
  setSpinnerVisibility: React.Dispatch<React.SetStateAction<boolean>>
) {
  setSpinnerVisibility(true);

  (async () => {
    const isLoggedOut = await Logout();
    //console.log("isLoggedOut " + isLoggedOut);
    if (isLoggedOut) {
      setShowAlert(false);
      setAlertMessage("");
      setSpinnerVisibility(false);
      window.location.replace(window.location.origin);
    } else {
      setShowAlert(true);
      setAlertMessage(ErrorsString.errorMessageLogout);
      setSpinnerVisibility(false);
    }
  })();
}
